import React, {
  ChangeEvent,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Stack,
  Modal,
  Table,
  Button,
} from "react-bootstrap";
import {
  FetchQMEDoctorScheduleList,
  FetchDoctorScheduleForCalendarCount,
  FetchQMEDoctorForToolTip,
} from "../../../../services/DoctorScheduleAPI";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import TextInput from "../../../../common/TextInput";
import SearchableSingleSelectDropdown from "../../../../common/SearchableDropdown";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchableMultiSelectDropdown from "../../../../common/SearchableMultiSelectDropdown";
import { fetcQMEDoctorHospitalList } from "../../../../services/ScheduleAppointmentApi";
import { getInitialDateValues, to12Hour } from "../../../../utils/dateUtils";
import DataTableForMultiAppointment from "../../../doctorScedule/manageScedule/components/DataTableForMultiAppointment";
import { ViewHeader } from "../../../doctorScedule/manageScedule/actions/constant";
import { exportToExcel } from "../../../../utils/exportToExcel";
import { exportToPDF } from "../../../../utils/exportToPDF";
import ViewScheduleDrawer from "./ViewScheduleDrawer";
import QMEScheduleOnCalenderView from "./QMEScheduleOnCalenderView";
import { setIsViewSchedule } from "../../../../redux/slice/CaseHeaderSlice";

const localizer = momentLocalizer(moment);
interface ViewScheduleProps {
  doctorId?: string;
  hospital?: string;
  month?: any;
  year?: any;
  onRowSelect?: (selectedRowData: any) => void;
  subTab?: any;
  doctorInfo?: any;
  onSlotClick?: (data: {
    showScheduleModal: boolean;
    selectedSlotData: any;
  }) => void;
  onCancel?: any;
}

const buildTrimmedAvailabilityEvents = (
  availItem: any,
  availStart: Date,
  availEnd: Date,
  appointmentsForDate: any[],
): any[] => {
  const availStartMs = availStart.getTime();
  const availEndMs = availEnd.getTime();

  const overlapping = appointmentsForDate
    .filter((appt) => {
      const s = new Date(appt.start).getTime();
      const e = new Date(appt.end).getTime();
      return s < availEndMs && e > availStartMs;
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  if (overlapping.length === 0) {
    return [
      {
        start: availStart,
        end: availEnd,
        title: "Availability",
        hospital: availItem.providerName || "",
        tooltip: `${moment(availStart).format("hh:mm A")} - ${moment(availEnd).format("hh:mm A")} ${availItem.providerName || ""}`,
        backgroundColor: "#35da12",
        contactMedicareListId: availItem.contactMedicareListId ?? null,
        medicareId: availItem.medicareId ?? null,
      },
    ];
  }

  const STEP_MS = 30 * 60 * 1000; // calendar step size: 30 min visual slot

  const merged: { start: number; end: number }[] = [];
  overlapping.forEach((appt) => {
    const apptStartMs = new Date(appt.start).getTime();
    const apptEndMs = new Date(appt.end).getTime();
    const s = Math.max(apptStartMs, availStartMs);
    // Extend to at least one step so availability gap matches the visual calendar slot
    const visualEnd = Math.max(apptEndMs, apptStartMs + STEP_MS);
    const e = Math.min(visualEnd, availEndMs);
    if (merged.length > 0 && s <= merged[merged.length - 1].end) {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, e);
    } else {
      merged.push({ start: s, end: e });
    }
  });

  const originalTooltip = `${moment(availStart).format("hh:mm A")} - ${moment(availEnd).format("hh:mm A")} ${availItem.providerName || ""}`;

  const results: any[] = [];
  let cursor = availStartMs;
  let firstEmitted = false;

  for (const interval of merged) {
    if (cursor < interval.start) {
      results.push({
        start: new Date(cursor),
        end: new Date(interval.start),
        title: "Availability",
        hospital: availItem.providerName || "",
        tooltip: originalTooltip,
        originalStart: availStart,
        originalEnd: availEnd,
        hideLabel: firstEmitted,
        backgroundColor: "#35da12",
        contactMedicareListId: availItem.contactMedicareListId ?? null,
        medicareId: availItem.medicareId ?? null,
      });
      firstEmitted = true;
    }
    cursor = interval.end;
  }

  if (cursor < availEndMs) {
    results.push({
      start: new Date(cursor),
      end: availEnd,
      title: "Availability",
      hospital: availItem.providerName || "",
      tooltip: originalTooltip,
      originalStart: availStart,
      originalEnd: availEnd,
      hideLabel: firstEmitted,
      backgroundColor: "#35da12",
      contactMedicareListId: availItem.contactMedicareListId ?? null,
      medicareId: availItem.medicareId ?? null,
    });
  }

  return results;
};

const CustomToolbar = (toolbar: any) => {
  const goToBack = () => toolbar.onNavigate("PREV");
  const goToNext = () => toolbar.onNavigate("NEXT");
  const goToToday = () => toolbar.onNavigate("TODAY");
  const handleViewChange = (view: string) => toolbar.onView(view);
  const formattedLabel =
    toolbar.view === "month"
      ? moment(toolbar.date).format("MMM YYYY")
      : toolbar.view === "week"
      ? `${moment(toolbar.date).startOf("week").format("MMM D")} – ${moment(toolbar.date).endOf("week").format("MMM D, YYYY")}`
      : moment(toolbar.date).format("ddd, MMM D YYYY");

  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="w-50 d-flex justify-content-between align-items-center">
        <button className="btn btn-sm btn-outline-primary" onClick={goToToday}>
          Today
        </button>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-sm btn-outline-secondary me-2 border-0 d-flex align-items-center material-icons-large"
            onClick={goToBack}
          >
            <span className="material-icons material-symbols-outlined arrow_left color_brand mx-1">
              keyboard_arrow_left
            </span>
          </button>
          <h5 className="m-0">{formattedLabel}</h5>
          <button
            className="btn btn-sm btn-outline-secondary ms-2 border-0 d-flex align-items-center material-icons-large"
            onClick={goToNext}
          >
            <span className="material-icons material-symbols-outlined arrow_left color_brand mx-1">
              keyboard_arrow_right
            </span>
          </button>
        </div>
      </div>
      <div className="ms-auto">
        <div className="btn-group">
          {["month", "week", "day"].map((view) => (
            <button
              key={view}
              className={`btn btn-sm ${
                toolbar.view === view ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => handleViewChange(view)}
            >
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ViewQMESchedule: React.FC<ViewScheduleProps> = ({
  hospital,
  month,
  year,
  onRowSelect,
  subTab,
  doctorInfo,
  onSlotClick,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const doctorId = doctorInfo?.id;
  const appointmentDate = doctorInfo?.appointmentDate;
  const initialDateValues = getInitialDateValues(appointmentDate);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    initialDateValues?.month,
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    initialDateValues?.year,
  );
  const [currentDate, setCurrentDate] = useState(new Date(appointmentDate));
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedHospitals, setSelectedHospitals] = useState<number[]>([]);
  const [calendarView, setCalendarView] = useState(true);
  const [hospitalList, setHospitalList] = useState<
    { label: string; value: string }[]
  >([]);
  const [viewMode, setViewMode] = useState<"month" | "week" | "day">("day");
  const [selectedEvents, setSelectedEvents] = useState<any[]>([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [allAppointmentEvents, setAllAppointmentEvents] = useState<any[]>([]);
  const [minTime, setMinTime] = useState(new Date(0, 0, 0, 0, 0));
  const [maxTime, setMaxTime] = useState(new Date(0, 0, 0, 23, 59));
  const [doctorName, setDoctorName] = useState<string>("");
  const [clickedSlot, setClickedSlot] = useState<any>(null);
  const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
  const [selectedDetailsIds, setSelectedDetailsIds] = useState<string[]>([]);
  const [showViewScheduleDrawer, setShowViewScheduleDrawer] =
    useState<boolean>(false);
  const [selectedSlotData, setSelectedSlotData] = useState<any>(null);
  const slotClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSlotInfo = useRef<any>(null);
  const eventClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingEventInfo = useRef<any>(null);
  const mouseDownTimeRef = useRef<{ start: Date; end: Date } | null>(null);
  const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null);
  const dragSelectedSlotRef = useRef<{ start: Date; end: Date } | null>(null);
  const [highlightedSlot, setHighlightedSlot] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [hoveredSlot, setHoveredSlot] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const hoveredSlotRef = useRef<{ start: Date; end: Date } | null>(null);
  const [rightClickMenu, setRightClickMenu] = useState<{
    x: number;
    y: number;
    start: Date;
    end: Date;
  } | null>(null);

  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const hospitals = await fetcQMEDoctorHospitalList(doctorId);
        if (Array.isArray(hospitals)) {
          const formattedHospitals = hospitals.map((hospital: any) => ({
            label: hospital.providerName,
            value: hospital.medicareId,
          }));
          setHospitalList((prevList) =>
            JSON.stringify(prevList) !== JSON.stringify(formattedHospitals)
              ? formattedHospitals
              : prevList,
          );
        } else {
          console.error("Unexpected response format:", hospitals);
        }
      } catch (error) {
        console.error("Error fetching hospital list:", error);
      }
    };
    if (doctorId) fetchHospitals();
  }, [doctorId]);

  const selectedHospitalIds = selectedHospitals.map(String).join(",");

  const handleHospitalChange = (
    event: ChangeEvent<HTMLSelectElement>,
    name: string,
    values: (string | number)[],
  ) => {
    setSelectedHospitals(values.map((value) => Number(value)));
  };

  const processEventsForCalendar = (
     rawEvents: any[],
     appointmentCounts: any[],
     mode: "month" | "week" | "day",
   ) => {
     const eventsByDate: Record<string, any[]> = {};
 
     rawEvents.forEach((item: any) => {
       const formattedDate = moment(
         item.dateOfAppointment.split(" - ")[0],
         "MM/DD/YYYY",
       ).format("YYYY-MM-DD");
 
       const startDateTime =
         item.calendarOptionType === "Non-Availability"
           ? moment(`${formattedDate} 00:00`, "YYYY-MM-DD hh:mm A").toDate()
           : moment(
               `${formattedDate} ${item.startTime}`,
               "YYYY-MM-DD hh:mm A",
             ).toDate();
 
       const endDateTime =
         item.calendarOptionType === "Non-Availability"
           ? moment(`${formattedDate} 23:59`, "YYYY-MM-DD hh:mm A").toDate()
           : moment(
               `${formattedDate} ${item.endTime}`,
               "YYYY-MM-DD hh:mm A",
             ).toDate();
 
       if (!eventsByDate[formattedDate]) eventsByDate[formattedDate] = [];
       eventsByDate[formattedDate].push({
         ...item,
         start: startDateTime,
         end: endDateTime,
       });
     });
 
     const nonAvailabilityEvents = rawEvents
       .filter((item: any) => item.calendarOptionType === "Non-Availability")
       .map((item: any) => {
         const formattedDate = moment(
           item.dateOfAppointment.split(" - ")[0],
           "MM/DD/YYYY",
         ).format("YYYY-MM-DD");
         return {
           start: moment(`${formattedDate} 00:00`, "YYYY-MM-DD hh:mm A").toDate(),
           end: moment(`${formattedDate} 23:59`, "YYYY-MM-DD hh:mm A").toDate(),
           title: "Non-Availability",
           hospital: item.providerName,
           tooltip: `Unavailable`,
           backgroundColor: "#EF373D",
         };
       });
 
     const fullAvailEvents = rawEvents
       .filter((item: any) => item.calendarOptionType === "Availability")
       .map((item: any) => {
         const formattedDate = moment(
           item.dateOfAppointment.split(" - ")[0],
           "MM/DD/YYYY",
         ).format("YYYY-MM-DD");
         const startDateTime = moment(
           `${formattedDate} ${item.startTime}`,
           "YYYY-MM-DD hh:mm A",
         ).toDate();
         const endDateTime = moment(
           `${formattedDate} ${item.endTime}`,
           "YYYY-MM-DD hh:mm A",
         ).toDate();
         return {
           start: startDateTime,
           end: endDateTime,
           title: "Availability",
           hospital: item.providerName || "",
           tooltip: `${moment(startDateTime).format("hh:mm A")} - ${moment(endDateTime).format("hh:mm A")} ${item.providerName || ""}`,
           backgroundColor: "#35da12",
           contactMedicareListId: item.contactMedicareListId ?? null,
           medicareId: item.medicareId ?? null,
         };
       });
 
     if (mode === "month") {
       const eventsNew: any[] = [];
       const summaryEvents = appointmentCounts
         .map((countItem: any) => {
           const formattedDate = moment(countItem.appointmentDate).format(
             "YYYY-MM-DD",
           );
           const dayEvents = (eventsByDate[formattedDate] || []).filter(
             (e: any) => e.calendarOptionType === "Appointment",
           );
           if (dayEvents.length === 0) return null;
 
           dayEvents.forEach((e: any, index: number) => {
             eventsNew.push({
               start: new Date(e.start),
               end: new Date(e.end),
               title: "Appointment",
               hospital: e.providerName,
               tooltip: `${index + 1}. ${e.providerName} | ${e.patientName} | ${moment(e.start).format("hh:mm A")} - ${moment(e.end).format("hh:mm A")}`,
               type: "Appointment",
               backgroundColor: "#007bff",
               patientName: e.patientName,
               qmeDoctorContactId: e.doctorId,
               examType: e.examType,
               locationAddress: e.locationAddress,
               doctor: e.doctorName,
               time: e.startTime,
               location: e.location,
               subjectQMEScheduleId: e.subjectQMEScheduleId,
               contactMedicareListId: e.contactMedicareListId ?? null,
               medicareId: e.medicareId ?? null,
             });
           });
 
           const startDateTime = new Date(
             Math.min(...dayEvents.map((e: any) => new Date(e.start).getTime())),
           );
           const endDateTime = new Date(
             Math.max(...dayEvents.map((e: any) => new Date(e.end).getTime())),
           );
           const tooltipText = dayEvents
             .map((e: any, i: number) => `${i + 1}.  ${e.providerName}`)
             .join("\n");
 
           return {
             start: startDateTime,
             end: endDateTime,
             title: `${countItem.totalAppointments} Appointment${
               countItem.totalAppointments > 1 ? "s" : ""
             }`,
             hospital: dayEvents[0]?.providerName || "",
             tooltip: tooltipText,
             type: "AppointmentSummary",
             backgroundColor: "#007bff",
           };
         })
         .filter(Boolean);
 
       setAllAppointmentEvents(eventsNew);
 
       const availByDateMap: Record<string, any[]> = {};
       fullAvailEvents.forEach((event: any) => {
         const dateKey = moment(event.start).format("YYYY-MM-DD");
         if (!availByDateMap[dateKey]) availByDateMap[dateKey] = [];
         availByDateMap[dateKey].push(event);
       });
 
       const uniqueAvailEvents: any[] = [];
       Object.entries(availByDateMap).forEach(([, events]) => {
         const startDateTime = new Date(
           Math.min(...events.map((e: any) => new Date(e.start).getTime())),
         );
         const endDateTime = new Date(
           Math.max(...events.map((e: any) => new Date(e.end).getTime())),
         );
         const tooltipText = events
           .map(
             (e: any, i: number) =>
               `${i + 1}. ${moment(e.start).format("hh:mm A")} - ${moment(e.end).format("hh:mm A")} Available at ${e.hospital}`,
           )
           .join("\n");
         uniqueAvailEvents.push({
           ...events[0],
           start: startDateTime,
           end: endDateTime,
           tooltip: tooltipText,
         });
       });
 
       return [...uniqueAvailEvents, ...summaryEvents, ...nonAvailabilityEvents];
     } else {
       const appointmentsByDate: Record<string, any[]> = {};
       rawEvents
         .filter((item: any) => item.calendarOptionType === "Appointment")
         .forEach((item: any) => {
           const formattedDate = moment(
             item.dateOfAppointment.split(" - ")[0],
             "MM/DD/YYYY",
           ).format("YYYY-MM-DD");
           const startDateTime = moment(
             `${formattedDate} ${item.startTime}`,
             "YYYY-MM-DD hh:mm A",
           ).toDate();
           const endDateTime = moment(
             `${formattedDate} ${item.endTime}`,
             "YYYY-MM-DD hh:mm A",
           ).toDate();
           if (!appointmentsByDate[formattedDate])
             appointmentsByDate[formattedDate] = [];
           appointmentsByDate[formattedDate].push({
             ...item,
             start: startDateTime,
             end: endDateTime,
           });
         });
 
       const availEvents: any[] = [];
       rawEvents
         .filter((item: any) => item.calendarOptionType === "Availability")
         .forEach((item: any) => {
           const formattedDate = moment(
             item.dateOfAppointment.split(" - ")[0],
             "MM/DD/YYYY",
           ).format("YYYY-MM-DD");
           const availStart = moment(
             `${formattedDate} ${item.startTime}`,
             "YYYY-MM-DD hh:mm A",
           ).toDate();
           const availEnd = moment(
             `${formattedDate} ${item.endTime}`,
             "YYYY-MM-DD hh:mm A",
           ).toDate();
           const segments = buildTrimmedAvailabilityEvents(
             item,
             availStart,
             availEnd,
             appointmentsByDate[formattedDate] || [],
           );
           availEvents.push(...segments);
         });
 
       const apptByStartKey = new Map<string, any[]>();
       rawEvents
         .filter((item: any) => item.calendarOptionType === "Appointment")
         .forEach((item: any) => {
           const formattedDate = moment(
             item.dateOfAppointment.split(" - ")[0],
             "MM/DD/YYYY",
           ).format("YYYY-MM-DD");
           const startDateTime = moment(
             `${formattedDate} ${item.startTime}`,
             "YYYY-MM-DD hh:mm A",
           ).toDate();
           const endDateTime = moment(
             `${formattedDate} ${item.endTime}`,
             "YYYY-MM-DD hh:mm A",
           ).toDate();
           const key = moment(startDateTime).format("YYYY-MM-DD HH:mm");
           if (!apptByStartKey.has(key)) apptByStartKey.set(key, []);
           apptByStartKey.get(key)!.push({ ...item, start: startDateTime, end: endDateTime });
         });
 
       const appointmentEvents: any[] = [];
       apptByStartKey.forEach((group) => {
         const blockStart = new Date(
           Math.min(...group.map((a) => new Date(a.start).getTime())),
         );
         const blockEnd = new Date(
           Math.max(...group.map((a) => new Date(a.end).getTime())),
         );
         const tooltipText = group
           .map((a, idx) =>
             `${idx + 1}. ${moment(a.start).format("hh:mm A")} - ${moment(a.end).format("hh:mm A")} | ${a.providerName || ""} | ${a.patientName || ""}`,
           )
           .join("\n");
         const rep = group[0];
         appointmentEvents.push({
           start: blockStart,
           end: blockEnd,
           title: "Appointment",
           hospital: rep.providerName || "",
           tooltip: tooltipText,
           backgroundColor: "#007bff",
           patientName: rep.patientName,
           qmeDoctorContactId: rep.doctorId,
           examType: rep.examType,
           locationAddress: rep.locationAddress,
           doctor: rep.doctorName,
           time: rep.startTime,
           location: rep.location,
           subjectQMEScheduleId: rep.subjectQMEScheduleId,
           contactMedicareListId: rep.contactMedicareListId ?? null,
           medicareId: rep.medicareId ?? null,
           _mergedAppointments: group?.length > 1
             ? group.map((a: any) => ({
                 start: a.start,
                 end: a.end,
                 title: "Appointment",
                 hospital: a.providerName || "",
                 backgroundColor: "#007bff",
                 patientName: a.patientName,
                 qmeDoctorContactId: a.doctorId,
                 examType: a.examType,
                 locationAddress: a.locationAddress,
                 doctor: a.doctorName,
                 time: a.startTime,
                 location: a.location,
                 subjectQMEScheduleId: a.subjectQMEScheduleId,
                 contactMedicareListId: a.contactMedicareListId ?? null,
                 medicareId: a.medicareId ?? null,
               }))
             : undefined,
         });
       });
 
       return [...availEvents, ...appointmentEvents, ...nonAvailabilityEvents];
     }
   };

  const fetchScheduleCalendar = async () => {
    try {
      const formattedMonth = selectedMonth.slice(0, 3);

      const response = await FetchQMEDoctorScheduleList(
        doctorId,
        selectedYear,
        formattedMonth,
        selectedHospitalIds,
      );

      const countResponse = await FetchDoctorScheduleForCalendarCount(
        doctorId,
        selectedYear,
        formattedMonth,
        selectedHospitalIds,
      );

      const appointmentCounts = countResponse?.result || [];

      if (response?.isSuccess && Array.isArray(response.result)) {
        const allEvents = processEventsForCalendar(
          response.result,
          appointmentCounts,
          viewMode,
        );
        setCalendarEvents(allEvents);
      } else {
        console.warn("No valid events found.");
      }
    } catch (err) {
      console.error("Error fetching calendar events:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduleCalendar();
  }, [viewMode, selectedMonth, selectedYear, selectedHospitalIds, doctorId]);

  useEffect(() => {
    setFilteredEvents(calendarEvents);
  }, [calendarEvents]);

  useEffect(() => {
    if (!rightClickMenu) return;
    const close = () => {
      setRightClickMenu(null);
    };
    window.addEventListener("wheel", close, { passive: true });
    return () => window.removeEventListener("wheel", close);
  }, [rightClickMenu]);

  useEffect(() => {
    if (viewMode === "month" || viewMode === "week" || viewMode === "day") {
      setFilteredEvents(
        calendarEvents.filter((event) => {
          const eventDate = new Date(event.start);
          return (
            eventDate.getFullYear() === selectedYear &&
            eventDate.toLocaleString("default", { month: "long" }) ===
              selectedMonth
          );
        }),
      );
    } else {
      setFilteredEvents(calendarEvents);
    }
  }, [viewMode, calendarEvents, selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchScheduleData = async () => {
      setLoading(true);
      if (calendarView) await fetchScheduleCalendar();
      setLoading(false);
    };
    fetchScheduleData();
  }, [
    calendarView,
    doctorId,
    selectedYear,
    selectedMonth,
    selectedHospitalIds,
  ]);

  const findEventsForDate = (date: Date, events: any[]) => {
    return events.filter((event) => {
      const eventDate = new Date(event.start);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const isAppointmentEvent = (ev: any) =>
    ev.type === "AppointmentSummary" ||
    ev.title === "Appointment" ||
    ev.type === "Appointment";

  const getOverlappingEventIds = (slotStart: any, slotEnd: any) => {
    const slotDateKey = moment(slotStart).format("YYYY-MM-DD");

    const toLocalMinutes = (date: any) => {
      const d = moment(date);
      return d.hours() * 60 + d.minutes();
    };

    const slotStartLocalMin = toLocalMinutes(slotStart);
    const slotEndLocalMin   = toLocalMinutes(slotEnd);
    const isMonthView = viewMode === "month";

    const isSameDay = (ev: any) =>
      moment(ev.start).format("YYYY-MM-DD") === slotDateKey ||
      moment(ev.end).format("YYYY-MM-DD") === slotDateKey;

    const isStrictOverlap = (ev: any): boolean => {
      const evStartLocalMin = toLocalMinutes(ev.start);
      const evEndLocalMin   = toLocalMinutes(ev.end);

      if (evEndLocalMin <= evStartLocalMin) return false;

      return evStartLocalMin < slotEndLocalMin && evEndLocalMin > slotStartLocalMin;
    };

    const isMatch = (ev: any): boolean =>
      isMonthView
        ? isSameDay(ev)
        : isSameDay(ev) && isStrictOverlap(ev);

    const findMatch = (candidates: any[]) =>
      candidates.find((ev) => isMatch(ev)) ?? null;

    const availMatch = findMatch(
      calendarEvents.filter((ev) => ev.title === "Availability"),
    );
    if (availMatch)
      return {
        contactMedicareListId: availMatch.contactMedicareListId ?? null,
        medicareId: availMatch.medicareId ?? null,
      };

    const apptSource = isMonthView
      ? [...allAppointmentEvents, ...calendarEvents.filter((ev) => isAppointmentEvent(ev))]
      : calendarEvents.filter((ev) => isAppointmentEvent(ev));
    
    const apptMatch = findMatch(apptSource);
    if (apptMatch)
      return {
        contactMedicareListId: apptMatch.contactMedicareListId ?? null,
        medicareId: apptMatch.medicareId ?? null,
      };

    return {};
  };

  const getEventTooltip = (event: any, viewMode: string): string => {
    const start = moment(event.start).format("hh:mm A");
    const end = moment(event.end).format("hh:mm A");
    const location = event.hospital || "No location";
    const timeRange = `${start} - ${end}`;

    if (event.title === "Appointment") {
      const cleanedHospital = event.hospital
        ?.replace(/^\d{2}:\d{2} - \d{2}:\d{2}\s*/, "")
        .trim();
      return `${timeRange} ${cleanedHospital}`;
    }
    if (event.title === "Availability") return event.tooltip || `${timeRange} ${location}`;
    if (event.title === "Non-Availability")
      return `Unavailable ${timeRange}`;
    return `${timeRange} ${location}`;
  };

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
    setSelectedMonth(date.toLocaleString("default", { month: "long" }));
    setSelectedYear(date.getFullYear());
  };

  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setSelectedEvents([]);
    setSelectedDetailsIds([]);
  };

  const handleSelectEvent = (event: any, multiAppointments: any) => {
    setDoctorName(doctorInfo?.name || "NA");
    setSelectedEvents(multiAppointments);
    setSelectedDetailsIds([]);
    setShowEventModal(true);
  };

  const handleSlotClickWithIcon = (
    slotInfo: any,
    isDoubleClick = false,
    isNonAvailability = false,
  ) => {
    const now = new Date();
    const slotStart = new Date(slotInfo.start);
    const slotEnd = new Date(slotInfo.end);
    const todayMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const slotMidnight = new Date(
      slotStart.getFullYear(),
      slotStart.getMonth(),
      slotStart.getDate(),
    );

    const isPast =
      viewMode === "month" ? slotMidnight < todayMidnight : slotEnd <= now;

    if (isPast || isNonAvailability || !doctorId) {
      setClickedSlot(null);
      return;
    }
  
    const overlappingIds =
      slotInfo.medicareId !== null
        ? {
            contactMedicareListId: slotInfo.contactMedicareListId ?? null,
            medicareId: slotInfo.medicareId ?? null,
          }
        : getOverlappingEventIds(slotStart, slotEnd);

    const slotData = {
      startDate: slotStart,
      endDate: slotEnd,
      viewMode,
      doctorId,
      doctorName: doctorInfo?.name,
      selectedHospitalIds: selectedHospitals,
      selectedMonth,
      selectedYear,
      isMultiAppointment: slotInfo.isMultiAppointment ?? false,
      ...overlappingIds,
    };

    if (isDoubleClick) {
      if (viewMode === "month") {
        // Month view double-click: navigate to day view for the clicked date
        setCurrentDate(slotStart);
        setViewMode("day");
        // ViewScheduleDrawer for month view double-click (do not remove):
        // setSelectedSlotData(slotData);
        // setShowViewScheduleDrawer(true);
      } else {
        setSelectedSlotData(slotData);
        setShowViewScheduleDrawer(true);
      }
      setRightClickMenu(null);
      dragSelectedSlotRef.current = null;
    }
  };

  const handleExportExcel = async () => {
    const appointmentDateLabel = selectedEvents.length
      ? moment(selectedEvents[0].start).format("MM/DD/YYYY")
      : "";

    const filteredEvts = selectedEvents.filter((event) =>
      selectedDetailsIds.includes(String(event.subjectQMEScheduleId)),
    );

    const exportData = filteredEvts.map((event, index) => ({
      "Sr. No": index + 1,
      "Patient Name": event.patientName || "N/A",
      Doctor: event.doctor || "N/A",
      "Exam Type": event.examType || "N/A",
      Location:
        `${event.location || ""} ${event.locationAddress ? `(${event.locationAddress})` : ""}` ||
        "N/A",
      Time: event.time ? to12Hour(event.time.slice(0, 5)) : "N/A",
    }));

    await exportToExcel({
      fileName: `Appointment Details for ${appointmentDateLabel}`,
      sheetName: "Appointment Details",
      title: "Appointment Details",
      titleDate: appointmentDateLabel,
      data: exportData,
    });
  };

  const handleExportPDF = async () => {
    const appointmentDateLabel = selectedEvents.length
      ? moment(selectedEvents[0].start).format("MM/DD/YYYY")
      : "";

    const filteredEvts = selectedEvents.filter((event) =>
      selectedDetailsIds.includes(String(event.subjectQMEScheduleId)),
    );

    const exportData = filteredEvts.map((event, index) => [
      index + 1,
      event.patientName || "N/A",
      event.doctor || "N/A",
      event.examType || "N/A",
      `${event.location || ""} ${event.locationAddress ? `(${event.locationAddress})` : ""}` ||
        "N/A",
      event.time ? to12Hour(event.time.slice(0, 5)) : "N/A",
    ]);

    const headers = [
      "Sr. No",
      "Patient Name",
      "Doctor",
      "Exam Type",
      "Location",
      "Time",
    ];
    const title = `Appointment Details for ${appointmentDateLabel}`;
    await exportToPDF(title, headers, exportData);
  };

  const dynamicTooltipMap = useRef<Record<string, string>>({});
  const fetchedTooltipKeys = useRef<Set<string>>(new Set());

  const eventKey = (event: any) =>
    `${event.type}_${moment(event.start).format("YYYYMMDD")}_${event.doctorId ?? ""}`;

  const resolveTooltip = (event: any): string => {
    const key = eventKey(event);
    if (dynamicTooltipMap.current[key]) return dynamicTooltipMap.current[key];
    if (viewMode === "month" && event.tooltip) return event.tooltip;
    return getEventTooltip(event, viewMode);
  };

  const handleEventMouseEnter = async (
    event: any,
    domNode: HTMLElement | null,
  ) => {
    const isSummaryEvent =
      event.type === "AppointmentSummary" || event.title === "Availability";

    if (!isSummaryEvent || !event.doctorId || !event.medicareProviderIds)
      return;

    const key = eventKey(event);
    if (fetchedTooltipKeys.current.has(key)) return;
    fetchedTooltipKeys.current.add(key);

    try {
      const date = moment(event.start);
      const providerIds = Array.isArray(event.medicareProviderIds)
        ? event.medicareProviderIds.join(",")
        : event.medicareProviderIds;

      const response = await FetchQMEDoctorForToolTip(
        event.doctorId,
        date.year(),
        date.format("MM"),
        date.format("DD"),
        providerIds,
      );

      if (response?.isSuccess) {
        const tooltipText = response.result
          .map(
            (item: any, index: number) =>
              `${index + 1}. ${item.startTime} - ${item.endTime} ${item.providerName}`,
          )
          .join("\n");

        dynamicTooltipMap.current[key] = tooltipText;

        if (domNode) domNode.title = tooltipText;
      }
    } catch (err) {
      console.error("Failed to fetch tooltip", err);
      fetchedTooltipKeys.current.delete(key);
    }
  };

  const finalEvents = [...calendarEvents];

  const appointmentDateLabel =
    selectedEvents.length > 0
      ? moment(selectedEvents[0].start).format("MM/DD/YYYY")
      : "";

  const modalRows = selectedEvents.map((event) => ({
    id: event.subjectQMEScheduleId,
    patientName: event.patientName || "",
    doctor: event.doctor || "",
    examType: event.examType || "",
    location: `${event.location || ""} ${
      event.locationAddress ? `(${event.locationAddress})` : ""
    }`,
    time: event.time ? to12Hour(event.time.slice(0, 5)) : "",
  }));

  const handleActionDrawer = (reason?: string) => {
    setShowViewScheduleDrawer(false);
    setSelectedSlotData(null);
    setHighlightedSlot(null);
    setRightClickMenu(null);
    dragSelectedSlotRef.current = null;
    if (reason === "Submit") {
      fetchScheduleCalendar();
    }
  };

  const handleCloseButton = () => {
    dispatch(setIsViewSchedule(false));
    onCancel();
  };

  const applyMouseDownTime = (eventDate: Date): { start: Date; end: Date } => {
    const stored = mouseDownTimeRef.current;
    if (!stored) return { start: eventDate, end: eventDate };

    const start = new Date(eventDate);
    start.setHours(stored.start.getHours(), stored.start.getMinutes(), 0, 0);

    const end = new Date(eventDate);
    end.setHours(stored.end.getHours(), stored.end.getMinutes(), 0, 0);

    return { start, end };
  };

  const getSlotFromMouse = (
    clientX: number,
    clientY: number,
    fallbackDate?: Date,
  ): { start: Date; end: Date } | null => {
    const el = document.elementFromPoint(clientX, clientY);
    const dayCol = el?.closest(".rbc-day-slot") as HTMLElement | null;
    if (!dayCol) return null;
    const gutterGroups = Array.from(
      document.querySelectorAll(".rbc-time-gutter .rbc-timeslot-group")
    ) as HTMLElement[];

    let hours: number;
    let minutes: number;

    if (gutterGroups.length >= 2) {
      const parseLabel = (el: Element | null): number => {
        if (!el) return 0;
        const text = el.textContent?.trim() ?? "";
        const m = moment(text, ["hh:mm A", "h:mm A", "HH:mm"]);
        return m.isValid() ? m.hours() * 60 + m.minutes() : 0;
      };

      const firstLabel = gutterGroups[0].querySelector(".rbc-label");
      const lastLabel = gutterGroups[gutterGroups.length - 1].querySelector(".rbc-label");
      const startMinutes = parseLabel(firstLabel);
      const endMinutes = parseLabel(lastLabel) + 60; 
      const totalRenderedMinutes = endMinutes - startMinutes;

      const topOfFirst = gutterGroups[0].getBoundingClientRect().top;
      const bottomOfLast = gutterGroups[gutterGroups.length - 1].getBoundingClientRect().bottom;
      const totalPixels = bottomOfLast - topOfFirst;

      const relativeY = clientY - topOfFirst;
      const clickedMinutes = startMinutes + (relativeY / totalPixels) * totalRenderedMinutes;
      const snapped = Math.floor(clickedMinutes / 30) * 30;
      hours = Math.floor(snapped / 60);
      minutes = snapped % 60;
    } else {
      const rect = dayCol.getBoundingClientRect();
      const clickedMinutes = ((clientY - rect.top) / rect.height) * 24 * 60;
      const snapped = Math.floor(clickedMinutes / 30) * 30;
      hours = Math.floor(snapped / 60);
      minutes = snapped % 60;
    }

    let slotDate: Date = fallbackDate || currentDate;

    if (viewMode === "day") {
      slotDate = new Date(currentDate);
    } else {
      const dateAttr =
        dayCol.getAttribute("data-date") ||
        dayCol.closest("[data-date]")?.getAttribute("data-date");

      if (dateAttr) {
        slotDate = new Date(dateAttr);
      } else {
        const allDayCols = Array.from(document.querySelectorAll(".rbc-day-slot"));
        const colIndex = allDayCols.indexOf(dayCol);

        if (colIndex >= 0) {
          const dateHeaders = Array.from(document.querySelectorAll(".rbc-header"));
          if (dateHeaders[colIndex]) {
            const headerLink = dateHeaders[colIndex].querySelector("a, button, span");
            const headerText =
              headerLink?.textContent || dateHeaders[colIndex].textContent || "";
            const parsed = moment(headerText.trim(), [
              "ddd MM/DD",
              "dddd MM/DD",
              "ddd D",
              "dddd D",
            ]);
            if (parsed.isValid()) {
              slotDate = new Date(
                currentDate.getFullYear(),
                parsed.month() >= 0 ? parsed.month() : currentDate.getMonth(),
                parsed.date(),
              );
            } else {
              const weekStart = moment(currentDate).startOf("week");
              slotDate = weekStart.add(colIndex, "days").toDate();
            }
          } else {
            const weekStart = moment(currentDate).startOf("week");
            slotDate = weekStart.add(colIndex, "days").toDate();
          }
        }
      }
    }

    const start = new Date(slotDate);
    start.setHours(hours, minutes, 0, 0);

    const end = new Date(slotDate);
    end.setHours(hours, minutes + 30, 0, 0);

    return { start, end };
  };
  
  return (
    <>
      <div className="main_wrapper_section">
        <div className="col-md-12 d-flex">
          <Button
            variant="outline"
            className="d-flex align-items-center btn-text-border me-1 ms-auto mb-2 mt-2"
            onClick={handleCloseButton}
          >
            <span className="material-icons mr-2 ">arrow_back</span>
            Back
          </Button>
        </div>

        <div className="d-flex"></div>
        <Row className="mb-3">
          <Col xs={4}>
            <TextInput
              label="Doctor Name"
              name="doctorName"
              value={doctorInfo?.name || ""}
              onChange={() => {}}
              disabled
            />
          </Col>

          <Col md={2} className="selection_dropdown adj_label_lt">
            <SearchableSingleSelectDropdown
              label="Select Year"
              name="selectedYear"
              options={yearRange.map((yr) => ({
                label: yr.toString(),
                value: yr,
              }))}
              value={selectedYear}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                const newYear = Number(event.target.value);
                const newMonth = selectedMonth;
                setSelectedYear(newYear);
                const monthIndex = [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].indexOf(newMonth);
                let appointmentDay = 1;
                const parsedDate = getInitialDateValues(appointmentDate);
                if (parsedDate) appointmentDay = Number(parsedDate.day);
                const newDate = new Date(newYear, monthIndex, appointmentDay);
                setCurrentDate(newDate);
              }}
            />
          </Col>

          <Col xs={2} className="selection_dropdown adj_label_lt">
            <SearchableSingleSelectDropdown
              label="Select Month"
              name="selectedMonth"
              options={[
                { label: "January", value: "January" },
                { label: "February", value: "February" },
                { label: "March", value: "March" },
                { label: "April", value: "April" },
                { label: "May", value: "May" },
                { label: "June", value: "June" },
                { label: "July", value: "July" },
                { label: "August", value: "August" },
                { label: "September", value: "September" },
                { label: "October", value: "October" },
                { label: "November", value: "November" },
                { label: "December", value: "December" },
              ]}
              value={selectedMonth}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                const newMonth = event.target.value;
                const newYear = selectedYear;
                setSelectedMonth(newMonth);
                const monthIndex = [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].indexOf(newMonth);
                let appointmentDay = 1;
                const parsedDate = getInitialDateValues(appointmentDate);
                if (parsedDate) appointmentDay = Number(parsedDate.day);
                const newDate = new Date(newYear, monthIndex, appointmentDay);
                setCurrentDate(newDate);
              }}
            />
          </Col>

          <Col xs={4} className="selection_dropdown adj_label_lt">
            <SearchableMultiSelectDropdown
              label="Select Location"
              name="location"
              options={hospitalList}
              values={selectedHospitals || []}
              onChange={handleHospitalChange}
            />
          </Col>
        </Row>

        <Row className="mb-3"></Row>

        <>
          <Row className="position-relative">
            <Col>
              <div
                style={{ top: "10px", right: "220px" }}
                className="d-flex align-items-center justify-content-end float-end w-25 position-absolute"
              >
                <div className="d-flex align-items-center me-3">
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#69BAE0",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "5px",
                    }}
                  ></span>
                  <span>Appointment</span>
                </div>
                <div className="d-flex align-items-center me-3">
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#C5F8BB",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "5px",
                    }}
                  ></span>
                  <span>Available</span>
                </div>
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#f7968d",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "5px",
                    }}
                  ></span>
                  <span>Unavailable</span>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <div
                onMouseDown={(e) => {
                  mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
                  const slot = getSlotFromMouse(e.clientX, e.clientY);
                  if (slot) {
                    mouseDownTimeRef.current = {
                      start: new Date(
                        0,
                        0,
                        0,
                        slot.start.getHours(),
                        slot.start.getMinutes(),
                        0,
                      ),
                      end: new Date(
                        0,
                        0,
                        0,
                        slot.end.getHours(),
                        slot.end.getMinutes(),
                        0,
                      ),
                    };
                  } else {
                    mouseDownTimeRef.current = null;
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (viewMode === "month") return;
                  const el = document.elementFromPoint(e.clientX, e.clientY);
                  const existingSlot = dragSelectedSlotRef.current;
                  if (existingSlot) {
                    const now = new Date();
                    if (existingSlot.end <= now) return;

                    const isNA = calendarEvents.some(
                      (ev) =>
                        ev.title === "Non-Availability" &&
                        existingSlot.start >= new Date(ev.start) &&
                        existingSlot.start < new Date(ev.end),
                    );
                    if (isNA) return;
                    
                    setRightClickMenu({
                      x: e.clientX,
                      y: e.clientY,
                      start: existingSlot.start,
                      end: existingSlot.end,
                    });
                    return;
                  }

                  const slot = getSlotFromMouse(e.clientX, e.clientY);
                  if (!slot) return;

                  const now = new Date();
                  if (slot.end <= now) return;

                  const isNA = calendarEvents.some(
                    (ev) =>
                      ev.title === "Non-Availability" &&
                      slot.start >= new Date(ev.start) &&
                      slot.start < new Date(ev.end),
                  );
                  if (isNA) return;

                  setHighlightedSlot(slot);
                  setRightClickMenu({ x: e.clientX, y: e.clientY, ...slot });
                }}
                onMouseMove={(e) => {
                  if (viewMode === "month") return;
                  const slot = getSlotFromMouse(e.clientX, e.clientY);
                  if (!slot) return;
                  if (
                    !hoveredSlotRef.current ||
                    hoveredSlotRef.current.start.getTime() !== slot.start.getTime()
                  ) {
                    hoveredSlotRef.current = slot;
                    setHoveredSlot(slot);
                  }
                }}
                onMouseLeave={() => {
                  hoveredSlotRef.current = null;
                  setHoveredSlot(null);
                }}
              >
                <Calendar
                  key={selectedMonth}
                  localizer={localizer}
                  events={finalEvents}
                  date={currentDate}
                  onNavigate={handleNavigate}
                  startAccessor="start"
                  endAccessor="end"
                  titleAccessor={(event) => {
                    if (viewMode === "month") {
                      if (event.title === "Availability") return "Available";
                      if (event.title === "Non-Availability")
                        return "Unavailable";
                      return event.title;
                    }
                    if (
                      event.title === "Non-Availability" ||
                      event.calendarOptionType === "Non-Availability"
                    ) {
                      return "";
                    }
                    if (event.title === "Appointment") {
                      return `${event.hospital}`;
                    }
                    if (event.hideLabel) return "";
                    const displayStart = event.originalStart ?? event.start;
                    const displayEnd = event.originalEnd ?? event.end;
                    const timeRange = `${moment(displayStart).format("hh:mm A")} - ${moment(displayEnd).format("hh:mm A")}`;
                    return `${timeRange} ${event.hospital}`;
                  }}
                  tooltipAccessor={(event) => resolveTooltip(event)}
                  style={{ height: 600 }}
                  views={["month", "week", "day"]}
                  view={viewMode}
                  onView={(newView) =>
                    setViewMode(newView as "month" | "week" | "day")
                  }
                  eventPropGetter={(event) => {
                    const isAppointment = event.title
                      ?.toLowerCase()
                      .includes("appointment");
                    const isAvailability = event.title === "Availability";
                    const isNonAvailability =
                      event.title === "Non-Availability";

                    let backgroundColor = "";
                    if (isAvailability)
                      backgroundColor = "rgba(197, 248, 187, 0.6)";
                    else if (isNonAvailability)
                      backgroundColor = "rgba(247, 150, 141, 0.6)";
                    else if (isAppointment)
                      backgroundColor = "rgba(105, 186, 224, 0.6)";

                    return {
                      onMouseOver: (e: React.MouseEvent<HTMLElement>) => {
                        handleEventMouseEnter(event, e.currentTarget);
                      },
                      style: {
                        backgroundColor,
                        borderRadius: "5px",
                        color: "#000",
                        textAlign: "left",
                        cursor: "pointer",
                        display: "block",
                        padding: "2px 6px",
                        height: "calc(100% - 2px)",
                        marginTop: "1px",
                        boxSizing: "border-box",
                        border: "1px solid transparent",
                      },
                    };
                  }}
                  selectable
                  longPressThreshold={0}
                  formats={{
                    eventTimeRangeFormat: () => "",
                    dayFormat: (date, culture, localizer) =>
                      localizer
                        ? localizer.format(date, "dddd, MMMM D", culture)
                        : "",
                    timeGutterFormat: (date, culture, localizer) =>
                      localizer
                        ? localizer.format(date, "hh:mm A", culture)
                        : "",
                  }}
                  onSelectEvent={(event) => {
                    const isSummary = event.type === "AppointmentSummary";
                    const isNA = event.title === "Non-Availability";
                    const isAvailable = event.title === "Availability";
                    const isAppointment =
                      event.type === "AppointmentSummary" ||
                      event.title === "Appointment" ||
                      event.type === "Appointment";

                    if (isNA) return;

                    const handleSingleClick = () => {
                      if (isAvailable || isSummary) {
                        setCurrentDate(event.start);
                        setMinTime(new Date(0, 0, 0, 0, 0));
                        setMaxTime(new Date(0, 0, 0, 23, 59));

                        if (isAvailable) {
                          const { start, end } = applyMouseDownTime(
                            event.start,
                          );
                          setHighlightedSlot({ start, end });
                          dragSelectedSlotRef.current = { start, end };
                        }
                      }

                      if (isAppointment) {
                        let sameTimeAppointments: any[] = [];
                        if (event.type === "AppointmentSummary") {
                          sameTimeAppointments = allAppointmentEvents.filter(
                            (e) =>
                              moment(e.start).format("YYYY-MM-DD") ===
                              moment(event.start).format("YYYY-MM-DD"),
                          );
                        } else {
                          if (event._mergedAppointments?.length) {
                            sameTimeAppointments = event._mergedAppointments;
                          } else {
                            sameTimeAppointments = calendarEvents.filter(
                              (e) =>
                                moment(e.start).format("YYYY-MM-DD hh:mm A") ===
                                moment(event.start).format("YYYY-MM-DD hh:mm A"),
                            );
                          }
                        }
                        if (sameTimeAppointments?.length) {
                          handleSelectEvent(event, sameTimeAppointments);
                        }
                      }

                      if (isAvailable) {
                        const { start, end } = applyMouseDownTime(event.start);
                        handleSlotClickWithIcon(
                          {
                            start,
                            end,
                            contactMedicareListId: event.contactMedicareListId ?? null,
                            medicareId: event.medicareId ?? null,
                          },
                          false,
                          false,
                        );
                      }
                    };

                    const handleDoubleClick = () => {
                      if (isAvailable || isSummary) {
                        setCurrentDate(event.start);
                        setMinTime(new Date(0, 0, 0, 0, 0));
                        setMaxTime(new Date(0, 0, 0, 23, 59));
                      }

                      if (isAvailable || isAppointment) {
                        const { start, end: applyEnd } = applyMouseDownTime(event.start);
                        const end = (viewMode === "month" && (isAppointment || isAvailable)) ? new Date(event.end) : applyEnd;
                        setHighlightedSlot({ start, end });
                        let summaryIds: any = {};
                        let isMultiAppointment = false;
                        if (isAppointment) {
                          const apptRows = allAppointmentEvents.filter(
                            (e: any) =>
                              moment(e.start).format("YYYY-MM-DD") ===
                              moment(event.start).format("YYYY-MM-DD"),
                          );
                          isMultiAppointment = apptRows.length > 1;
                          summaryIds = {
                            contactMedicareListId:
                              apptRows.find((e: any) => e.contactMedicareListId)
                                ?.contactMedicareListId ?? null,

                            medicareId:
                              apptRows.find((e: any) => e.medicareId)
                                ?.medicareId ?? null,
                          };
                        }
                        handleSlotClickWithIcon(
                          {
                            start,
                            end,
                            contactMedicareListId: isAppointment
                              ? summaryIds?.contactMedicareListId
                              : (event.contactMedicareListId ?? null),
                            medicareId: isAppointment
                              ? summaryIds?.medicareId
                              : (event.medicareId ?? null),
                            isMultiAppointment,
                          },
                          true,
                          false,
                        );
                      }
                    };

                    if (
                      pendingEventInfo.current &&
                      moment(pendingEventInfo.current.start).isSame(
                        moment(event.start),
                        "minute",
                      ) &&
                      pendingEventInfo.current.title === event.title
                    ) {
                      if (eventClickTimer.current)
                        clearTimeout(eventClickTimer.current);
                      pendingEventInfo.current = null;
                      handleDoubleClick();
                    } else {
                      pendingEventInfo.current = {
                        start: event.start,
                        title: event.title,
                      };
                      if (eventClickTimer.current)
                        clearTimeout(eventClickTimer.current);
                      eventClickTimer.current = setTimeout(() => {
                        if (pendingEventInfo.current) {
                          handleSingleClick();
                          pendingEventInfo.current = null;
                        }
                      }, 300);
                    }
                  }}
                  onSelectSlot={(slotInfo) => {
                    const resolveSlot = (info: any, isDoubleClick: boolean) => {
                      if (viewMode === "month") {
                        const dateEvents = findEventsForDate(
                          info.start,
                          calendarEvents,
                        );

                        if (dateEvents.length > 0) {
                          const firstEvent = dateEvents[0];
                          const isSummary =
                            firstEvent.type === "AppointmentSummary";
                          const isNA = firstEvent.title === "Non-Availability";
                          const isAvailable =
                            firstEvent.title === "Availability";

                          if (isAvailable || isNA || isSummary) {
                            setCurrentDate(firstEvent.start);
                            setMinTime(new Date(0, 0, 0, 0, 0));
                            setMaxTime(new Date(0, 0, 0, 23, 59));
                          }

                          if (
                            firstEvent.type === "AppointmentSummary" ||
                            firstEvent.title === "Appointment"
                          ) {
                            let sameTimeAppointments: any[] = [];
                            if (firstEvent.type === "AppointmentSummary") {
                              sameTimeAppointments =
                                allAppointmentEvents.filter(
                                  (e) =>
                                    moment(e.start).format("YYYY-MM-DD") ===
                                    moment(firstEvent.start).format(
                                      "YYYY-MM-DD",
                                    ),
                                );
                            } else {
                              sameTimeAppointments = calendarEvents.filter(
                                (e) =>
                                  moment(e.start).format(
                                    "YYYY-MM-DD hh:mm A",
                                  ) ===
                                  moment(firstEvent.start).format(
                                    "YYYY-MM-DD hh:mm A",
                                  ),
                              );
                            }
                            if (sameTimeAppointments?.length)
                              handleSelectEvent(
                                firstEvent,
                                sameTimeAppointments,
                              );
                          } else if (isAvailable) {
                            handleSlotClickWithIcon(
                              { start: firstEvent.start, end: firstEvent.end },
                              isDoubleClick,
                              false,
                            );
                          } else if (isNA) {
                            setClickedSlot(null);
                          }
                        } else {
                          const startOfDay = new Date(info.start);
                          startOfDay.setHours(0, 0, 0, 0);
                          const endOfDay = new Date(info.start);
                          endOfDay.setHours(23, 59, 59, 999);
                          handleSlotClickWithIcon(
                            { start: startOfDay, end: endOfDay },
                            isDoubleClick,
                            false,
                          );
                        }
                      } else {
                        const overlappingNA = calendarEvents.some(
                          (e) =>
                            e.title === "Non-Availability" &&
                            new Date(info.start) >= new Date(e.start) &&
                            new Date(info.start) < new Date(e.end),
                        );
                        const dragStart = info.start;
                        const dragEnd = info.end;
                        dragSelectedSlotRef.current = {
                          start: dragStart,
                          end: dragEnd,
                        };
                        setHighlightedSlot({ start: dragStart, end: dragEnd });
                        handleSlotClickWithIcon(
                          { start: dragStart, end: dragEnd },
                          isDoubleClick,
                          overlappingNA,
                        );
                      }
                    };

                    if (
                      pendingSlotInfo.current &&
                      moment(pendingSlotInfo.current.start).isSame(
                        moment(slotInfo.start),
                        "minute",
                      )
                    ) {
                      if (slotClickTimer.current)
                        clearTimeout(slotClickTimer.current);
                      pendingSlotInfo.current = null;
                      resolveSlot(slotInfo, true);
                    } else {
                      pendingSlotInfo.current = slotInfo;
                      if (slotClickTimer.current)
                        clearTimeout(slotClickTimer.current);
                      slotClickTimer.current = setTimeout(() => {
                        if (pendingSlotInfo.current) {
                          resolveSlot(pendingSlotInfo.current, false);
                          pendingSlotInfo.current = null;
                        }
                      }, 300);
                    }
                  }}
                  min={minTime}
                  max={maxTime}
                  slotPropGetter={(date) => {
                    const now = new Date();
                    const isPast = date < now;

                    if (highlightedSlot) {
                      const d = new Date(date);
                      const s = new Date(highlightedSlot.start);
                      const e = new Date(highlightedSlot.end);
                      d.setSeconds(0, 0);
                      s.setSeconds(0, 0);
                      e.setSeconds(0, 0);
                      const isStartingSlotOfEvent = calendarEvents.some(
                        (event) => {
                          const eventStart = new Date(event.start);
                          eventStart.setSeconds(0, 0);
                          return d.getTime() === eventStart.getTime();
                        },
                      );

                      if (!isStartingSlotOfEvent && d >= s && d < e) {
                        const isFirst = d.getTime() === s.getTime();
                        return {
                          className: isFirst
                            ? `rbc-slot-highlighted rbc-slot-highlighted-${viewMode === "day" ? "first" : viewMode === "week" ? "Week-first" : "first"}`
                            : "rbc-slot-highlighted",
                          style: {
                            backgroundColor: "rgba(0, 123, 255, 0.20)",
                            borderLeft: "3px solid #007bff",
                            "--slot-time": `"${moment(highlightedSlot.start).format("hh:mm A")} - ${moment(highlightedSlot.end).format("hh:mm A")}"`,
                          } as React.CSSProperties,
                        };
                      }
                    }

                    if (hoveredSlot) {
                      const d = new Date(date);
                      const s = new Date(hoveredSlot.start);
                      const e = new Date(hoveredSlot.end);
                      d.setSeconds(0, 0);
                      s.setSeconds(0, 0);
                      e.setSeconds(0, 0);
                      if (d >= s && d < e) {
                        const isFirst = d.getTime() === s.getTime();
                        return {
                          className: isFirst
                            ? `rbc-slot-highlighted rbc-slot-highlighted-${viewMode === "day" ? "first" : viewMode === "week" ? "Week-first" : "first"}`
                            : "rbc-slot-highlighted",
                          style: {
                            backgroundColor: "rgba(0, 123, 255, 0.10)",
                            borderLeft: "3px solid rgba(0, 123, 255, 0.4)",
                            cursor: "pointer",
                            // "--slot-time": `"${moment(hoveredSlot.start).format("hh:mm A")} - ${moment(hoveredSlot.end).format("hh:mm A")}"`,
                          } as React.CSSProperties,
                        };
                      }
                    }

                    if (isPast) {
                      return {
                        style: {
                          backgroundColor: "rgba(200, 200, 200, 0.4)",
                        },
                      };
                    }

                    return {};
                  }}
                  dayPropGetter={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const dayDate = new Date(date);
                    dayDate.setHours(0, 0, 0, 0);
                    if (dayDate < today) {
                      return {
                        style: {
                          backgroundColor: "rgba(200, 200, 200, 0.3)",
                        },
                      };
                    }
                    return {};
                  }}
                  components={{
                    toolbar: CustomToolbar,
                    month: {
                      dateHeader: ({ date, label }: any) => <div>{label}</div>,
                    },
                  }}
                />
              </div>
            </Col>
          </Row>

          <Modal
            show={showEventModal}
            onHide={handleCloseEventModal}
            size="lg"
            className="app-detail recordList"
          >
            <Modal.Header closeButton className="d-flex m-1 p-1">
              <Modal.Title className="fw-bold text-left modelheading offcanvas-title d-flex align-items-center">
                <span className="appointment_title_modal">Appointments</span>
                &nbsp;
                <span className="doctorDetails_badge mb-0 appointment_title_modal">
                  <span>{doctorName}</span>
                </span>
                <span className="black_text">
                  Scheduled On: {appointmentDateLabel}
                </span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-left">
              {selectedEvents.length > 0 && (
                <DataTableForMultiAppointment
                  headers={ViewHeader}
                  rows={modalRows}
                  isCheckbox={true}
                  onCheckboxSelect={(row: any) => {
                    const ids = row
                      ?.map((r: any) => String(r?.id))
                      ?.filter(Boolean);
                    setSelectedDetailsIds(ids);
                  }}
                  selectedRowIds={selectedDetailsIds}
                  disabled={false}
                />
              )}
            </Modal.Body>
            <Modal.Footer className="border-top d-flex m-1 p-1">
              {selectedDetailsIds?.length > 0 && (
                <>
                  <Button
                    variant="primary"
                    className="d-flex align-items-center"
                    onClick={handleExportPDF}
                  >
                    <span className="material-icons mx-1">print</span> Print
                  </Button>
                  <Button
                    variant="primary"
                    className="d-flex align-items-center"
                    onClick={handleExportExcel}
                  >
                    <span className="material-icons material-symbols-outlined mx-1">
                      file_export
                    </span>{" "}
                    Export To Excel
                  </Button>
                </>
              )}
              <Button
                variant="outline-info"
                className="ml-3 d-flex align-items-center ms-auto"
                onClick={handleCloseEventModal}
              >
                <span className="material-icons mr-2">close</span> Close
              </Button>
            </Modal.Footer>
          </Modal>
        </>
        <ViewScheduleDrawer
          title={"Add Appointment"}
          open={showViewScheduleDrawer}
          onClose={handleActionDrawer}
          placement="end"
        >
          <QMEScheduleOnCalenderView
            slotData={selectedSlotData}
            onCancel={handleActionDrawer}
            isScheduleAppointmentView={true}
          />
        </ViewScheduleDrawer>
      </div>

      {(rightClickMenu && doctorId) && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
            onClick={() => {
              setRightClickMenu(null);
              setHighlightedSlot(null);
              dragSelectedSlotRef.current = null;
            }}
          />
          <div
            style={{
              position: "fixed",
              ...(rightClickMenu.x + 200 > window.innerWidth
                ? { right: `${window.innerWidth - rightClickMenu.x}px` }
                : { left: `${rightClickMenu.x}px` }),
              ...(rightClickMenu.y + 90 > window.innerHeight
                ? { bottom: `${window.innerHeight - rightClickMenu.y}px` }
                : { top: `${rightClickMenu.y}px` }),
              zIndex: 1000,
              background: "white",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              padding: "3px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: "#888",
                padding: "4px 10px 6px",
                borderBottom: "1px solid #eee",
                marginBottom: "4px",
              }}
            >
              {moment(rightClickMenu.start).format("hh:mm A")} –{" "}
              {moment(rightClickMenu.end).format("hh:mm A")}
            </div>
            <button
              onClick={() => {
                const overlappingIds = getOverlappingEventIds(
                  rightClickMenu.start,
                  rightClickMenu.end,
                );
                setSelectedSlotData({
                  startDate: rightClickMenu.start,
                  endDate: rightClickMenu.end,
                  viewMode,
                  doctorId,
                  doctorName: doctorInfo?.name,
                  selectedHospitalIds: selectedHospitals,
                  selectedMonth,
                  selectedYear,
                  ...overlappingIds,
                });
                setShowViewScheduleDrawer(true);
                setRightClickMenu(null);
                setHighlightedSlot(null);
                dragSelectedSlotRef.current = null;
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "7px 10px",
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                color: "#007bff",
                borderRadius: "6px",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f0f7ff")
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
            >
              <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span>
              Add Appointment
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default ViewQMESchedule;